import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowRight, GraduationCap, BookOpen, Users, Star, Award } from 'lucide-react';
import { logoImage } from '../config/brand';

const SubjectTeachersPage: React.FC = () => {
  const { subject } = useParams<{ subject: string }>();
  const navigate = useNavigate();

  // Subject data mapping
  const subjects = {
    chemistry: {
      name: 'كيمياء',
      icon: 'https://cdn.ednuva.com/Materials/2022-07/nWh1HHvYdDmefr9wbcq91HNs2Tyr0w5pu5Xyl2le.png',
      color: 'blue',
      description: 'تعلم الكيمياء مع أفضل المدرسين المتخصصين',
    },
    physics: {
      name: 'فيزياء',
      icon: 'https://cdn.ednuva.com/Materials/2022-07/LA7Kwue8nT622BUSn7PVQikTstngI7DLvMCGAMJH.png',
      color: 'green',
      description: 'أتقن الفيزياء مع مدرسين خبراء',
    },
    arabic: {
      name: 'لغة عربية',
      icon: 'https://cdn.ednuva.com/Materials/2022-07/uKWntv2HtpxZurqzGyMPOZJrWWLxz6YfDMHZfuKd.png',
      color: 'purple',
      description: 'تعلم اللغة العربية بطريقة صحيحة',
    },
    math: {
      name: 'الرياضيات',
      icon: 'https://cdn.ednuva.com/Materials/2022-07/EeFFhkl3frMa6CvSoNdQ3ZgfOxdXtZ6Wk9aW8xRh.png',
      color: 'orange',
      description: 'حل جميع مسائل الرياضيات بسهولة',
    },
    english: {
      name: 'اللغة الإنجليزية',
      icon: 'https://cdn.ednuva.com/Materials/2022-07/muT9yJlfSKc4V0MbpQcYlFzqgVQ4VLeFJX1I4g1p.png',
      color: 'red',
      description: 'تعلم الإنجليزية من الصفر للإتقان',
    },
    biology: {
      name: 'الأحياء',
      icon: 'https://cdn.ednuva.com/Materials/2022-07/8YBkTIW7EV65GXyMYgGS50UlLMnFKWZ0PbebURd5.png',
      color: 'teal',
      description: 'فهم علم الأحياء بطريقة تفاعلية',
    },
    geography: {
      name: 'جغرافيا',
      icon: 'https://cdn.ednuva.com/Materials/2022-07/XgGbvds9sIpFZtUAnDhMyFGqWrjMSfcoydIi8LeT.png',
      color: 'indigo',
      description: 'استكشف العالم من خلال الجغرافيا',
    },
    french: {
      name: 'اللغة الفرنسية',
      icon: 'https://cdn.ednuva.com/Materials/2022-07/LDpcofR3l9GUhi31QHy7mdUbhQ1QrVL82wxXqpGg.png',
      color: 'pink',
      description: 'تعلم الفرنسية مع مدرسين متخصصين',
    },
    german: {
      name: 'اللغة الألمانية',
      icon: 'https://cdn.ednuva.com/Materials/2022-07/9dX4xXiXGKZ1TG1q2z2jSgsYnlignhYJ2jvePHM7.png',
      color: 'yellow',
      description: 'أتقن الألمانية مع أفضل المدرسين',
    },
    italian: {
      name: 'اللغة الإيطالية',
      icon: 'https://cdn.ednuva.com/Materials/2022-07/9dX4xXiXGKZ1TG1q2z2jSgsYnlignhYJ2jvePHM7.png',
      color: 'cyan',
      description: 'تعلم الإيطالية بطريقة ممتعة',
    },
  };

  // Teachers data (placeholder logo until teacher photos are restored)
  const allTeachers = [
    { id: 1, name: 'أحمد علي', image: logoImage, speciality: 'اللغة الإنجليزية', subjects: ['english'], experience: '10+ سنوات', rating: 5.0, studentsCount: 500 },
    { id: 2, name: 'أحمد محمود', image: logoImage, speciality: 'الفيزياء', subjects: ['physics'], experience: '8+ سنوات', rating: 4.9, studentsCount: 400 },
    { id: 3, name: 'طارق أحمد', image: logoImage, speciality: 'الكيمياء', subjects: ['chemistry'], experience: '7+ سنوات', rating: 4.8, studentsCount: 350 },
    { id: 4, name: 'علي حسن', image: logoImage, speciality: 'الرياضيات', subjects: ['math'], experience: '12+ سنوات', rating: 5.0, studentsCount: 600 },
    { id: 5, name: 'نور الدين', image: logoImage, speciality: 'الأحياء', subjects: ['biology'], experience: '6+ سنوات', rating: 4.7, studentsCount: 300 },
  ];

  const currentSubject = subjects[subject as keyof typeof subjects];
  const subjectTeachers = allTeachers.filter((teacher) =>
    teacher.subjects.includes(subject as string)
  );

  const handleTeacherClick = (teacherId: number) => {
    navigate(`/teacher/${teacherId}`);
  };

  const getColorClasses = (color: string) => {
    const colorMap = {
      blue: 'from-blue-500 to-blue-600',
      green: 'from-green-500 to-green-600',
      purple: 'from-purple-500 to-purple-600',
      orange: 'from-orange-500 to-orange-600',
      red: 'from-red-500 to-red-600',
      teal: 'from-teal-500 to-teal-600',
      indigo: 'from-indigo-500 to-indigo-600',
      pink: 'from-pink-500 to-pink-600',
      yellow: 'from-yellow-500 to-yellow-600',
      cyan: 'from-cyan-500 to-cyan-600',
    };
    return colorMap[color as keyof typeof colorMap] || 'from-blue-500 to-blue-600';
  };

  if (!currentSubject) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">المادة غير موجودة</h1>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            العودة للرئيسية
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      <div className="relative overflow-hidden bg-gradient-to-br from-green-950 via-green-900 to-green-800 py-20 text-white">
        <div className="absolute inset-0 bg-gradient-to-br from-green-900/40 via-transparent to-gold-500/10" />

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate(-1)}
            className="mb-12 flex items-center gap-2 text-white/80 transition-all duration-200 hover:translate-x-1 hover:text-white"
          >
            <ArrowRight className="h-6 w-6" />
            <span className="font-medium">العودة</span>
          </button>

          <div className="text-center">
            <div className="mb-8 inline-flex items-center gap-4 rounded-2xl bg-white/10 px-6 py-4 backdrop-blur-sm">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/20">
                <img src={currentSubject.icon} alt={currentSubject.name} className="h-10 w-10" />
              </div>
              <div>
                <h1 className="bg-gradient-to-r from-white to-gold-100 bg-clip-text text-4xl font-bold text-transparent lg:text-6xl">
                  {currentSubject.name}
                </h1>
                <p className="mt-2 text-lg text-green-100">{currentSubject.description}</p>
              </div>
            </div>

            <div className="flex items-center justify-center gap-8 text-center">
              <div className="rounded-2xl border border-white/20 bg-white/10 p-6 backdrop-blur-sm">
                <div className="mb-2 flex items-center justify-center gap-3">
                  <Users className="h-8 w-8 text-gold-300" />
                  <span className="text-3xl font-bold text-white">{subjectTeachers.length}</span>
                </div>
                <p className="text-green-100">مدرس متخصص</p>
              </div>

              <div className="rounded-2xl border border-white/20 bg-white/10 p-6 backdrop-blur-sm">
                <div className="mb-2 flex items-center justify-center gap-3">
                  <Star className="h-8 w-8 text-gold-300" />
                  <span className="text-3xl font-bold text-white">4.9</span>
                </div>
                <p className="text-green-100">تقييم متوسط</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <h2 className="mb-6 text-4xl font-bold text-gray-900 lg:text-5xl">
              مدرسين متخصصين في {currentSubject.name}
            </h2>
            <p className="mx-auto max-w-2xl text-xl text-gray-600">
              اختر المدرس المناسب لك من بين أفضل المدرسين المتخصصين في {currentSubject.name}
            </p>
          </div>

          {subjectTeachers.length > 0 ? (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {subjectTeachers.map((teacher) => (
                <div
                  key={teacher.id}
                  onClick={() => handleTeacherClick(teacher.id)}
                  className="group relative cursor-pointer overflow-hidden rounded-2xl border border-gray-100 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-2 hover:border-green-200 hover:shadow-2xl"
                >
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${getColorClasses(currentSubject.color)} opacity-0 transition-opacity duration-300 group-hover:opacity-5`}
                  />

                  <div className="relative z-10">
                    <div className="mb-6 text-center">
                      <div className="mx-auto h-32 w-32 overflow-hidden rounded-2xl bg-green-50 shadow-lg ring-4 ring-gray-100 transition-all duration-300 group-hover:ring-green-200">
                        <img
                          src={teacher.image}
                          alt={teacher.name}
                          className="h-full w-full object-contain p-3"
                        />
                      </div>
                    </div>

                    <div className="mb-6 text-center">
                      <h3 className="mb-2 text-2xl font-bold text-gray-900 transition-colors duration-300 group-hover:text-green-700">
                        الأستاذ {teacher.name}
                      </h3>
                      <p className="mb-4 text-lg text-gray-600">{teacher.speciality}</p>

                      <div className="mb-4 flex items-center justify-center gap-2">
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`h-5 w-5 ${
                                i < Math.floor(teacher.rating)
                                  ? 'fill-current text-yellow-400'
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                        <span className="font-medium text-gray-600">{teacher.rating}</span>
                      </div>
                    </div>

                    <div className="mb-6 grid grid-cols-2 gap-4">
                      <div className="text-center">
                        <div className="mb-1 flex items-center justify-center gap-2">
                          <Users className="h-5 w-5 text-green-500" />
                          <span className="text-2xl font-bold text-gray-900">
                            {teacher.studentsCount}+
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">طالب ناجح</p>
                      </div>

                      <div className="text-center">
                        <div className="mb-1 flex items-center justify-center gap-2">
                          <Award className="h-5 w-5 text-gold-600" />
                          <span className="text-2xl font-bold text-gray-900">
                            {teacher.experience}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">خبرة</p>
                      </div>
                    </div>

                    <div
                      className={`flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r ${getColorClasses(currentSubject.color)} px-6 py-3 font-bold text-white shadow-md transition-all duration-300 hover:shadow-lg`}
                    >
                      <BookOpen className="h-5 w-5" />
                      <span>عرض الكورسات</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-20 text-center">
              <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-gray-100">
                <GraduationCap className="h-12 w-12 text-gray-400" />
              </div>
              <h3 className="mb-4 text-2xl font-bold text-gray-800">لا يوجد مدرسين متاحين</h3>
              <p className="mb-8 text-gray-600">
                عذراً، لا يوجد مدرسين متخصصين في هذه المادة حالياً
              </p>
              <button
                onClick={() => navigate('/')}
                className="rounded-lg bg-green-700 px-8 py-3 text-white transition-colors hover:bg-green-800"
              >
                العودة للرئيسية
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubjectTeachersPage;
